import React from "react";
import { useNavigate } from "react-router-dom";
import { ShieldAlert, Crown, ArrowLeft, Home, MessageCircle } from "lucide-react";

interface NoAccessScreenProps {
  /** Why access was denied */
  reason?: "plan_blocked" | "permission_denied";
  /** Display name of the feature/module being accessed */
  featureName?: string;
  /** Show as a card inside the page (true) vs fill the whole viewport (false) */
  embedded?: boolean;
}

/**
 * Unified "No Access" screen rendered when the user lacks a module or permission.
 *
 * Usage:
 *   <NoAccessScreen reason="permission_denied" featureName="Inventory" />
 *   <NoAccessScreen reason="plan_blocked" featureName="WhatsApp" embedded />
 */
const NoAccessScreen: React.FC<NoAccessScreenProps> = ({
  reason = "permission_denied",
  featureName,
  embedded = false,
}) => {
  const navigate = useNavigate();

  const isPlanBlocked = reason === "plan_blocked";

  const title = isPlanBlocked ? "Feature Not Available" : "Access Restricted";

  const subtitle = featureName
    ? isPlanBlocked
      ? `${featureName} is not included in your current plan.`
      : `You don't have permission to access ${featureName}.`
    : isPlanBlocked
      ? "This feature is not included in your current plan."
      : "You don't have permission to access this section.";

  const detail = isPlanBlocked
    ? "Upgrade your plan to unlock this feature for your team."
    : "Contact your administrator to request access.";

  const containerClass = embedded
    ? "flex flex-col items-center justify-center w-full py-20 px-4"
    : "flex flex-col items-center justify-center min-h-screen w-full bg-slate-50 px-4 py-12";

  return (
    <div className={containerClass}>
      {/* Card */}
      <div className="bg-white border border-slate-200 rounded-2xl shadow-xl shadow-slate-200/60 w-full max-w-md p-8 text-center">
        {/* Icon */}
        <div
          className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 ${
            isPlanBlocked
              ? "bg-amber-50 ring-4 ring-amber-100"
              : "bg-red-50 ring-4 ring-red-100"
          }`}
        >
          {isPlanBlocked ? (
            <Crown
              className="w-9 h-9 text-amber-500"
              strokeWidth={1.75}
            />
          ) : (
            <ShieldAlert
              className="w-9 h-9 text-red-500"
              strokeWidth={1.75}
            />
          )}
        </div>

        {/* Text */}
        <h2 className="text-xl font-bold text-slate-800 mb-2">{title}</h2>
        <p className="text-slate-600 text-sm font-medium leading-relaxed mb-1">
          {subtitle}
        </p>
        <p className="text-slate-400 text-sm leading-relaxed mb-8">{detail}</p>

        {/* Pill badge */}
        <div
          className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold mb-8 ${
            isPlanBlocked
              ? "bg-amber-50 text-amber-700 border border-amber-200"
              : "bg-red-50 text-red-600 border border-red-200"
          }`}
        >
          {isPlanBlocked ? (
            <>
              <Crown className="w-3 h-3" />
              Plan upgrade required
            </>
          ) : (
            <>
              <ShieldAlert className="w-3 h-3" />
              Permission required
            </>
          )}
        </div>

        {/* Actions */}
        <div className="space-y-3">
          <button
            onClick={() => navigate(-1)}
            className="w-full flex items-center justify-center gap-2 py-3 px-4 bg-slate-900 hover:bg-slate-800 text-white rounded-xl font-semibold text-sm transition-all active:scale-95 shadow-sm shadow-slate-900/20"
          >
            <ArrowLeft className="w-4 h-4" />
            Go Back
          </button>

          <button
            onClick={() => navigate("/")}
            className="w-full flex items-center justify-center gap-2 py-3 px-4 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-semibold text-sm transition-all active:scale-95"
          >
            <Home className="w-4 h-4" />
            Go to Home
          </button>

          {isPlanBlocked && (
            <button
              onClick={() => navigate("/billing-history")}
              className="w-full flex items-center justify-center gap-2 py-3 px-4 bg-amber-50 hover:bg-amber-100 text-amber-700 border border-amber-200 rounded-xl font-semibold text-sm transition-all active:scale-95"
            >
              <Crown className="w-4 h-4" />
              View Plans
            </button>
          )}
        </div>

        {/* Footer note */}
        <p className="mt-6 text-xs text-slate-400">
          {isPlanBlocked
            ? "Error 403 · Plan restriction"
            : "Error 403 · Contact your manager if this is a mistake"}
        </p>
      </div>
    </div>
  );
};

export default NoAccessScreen;
